import axios from 'axios'
import { User } from '../../types/auth'

// Determine API base URL at runtime
// In production (Cloudflare Pages), always use relative /api path (handled by Cloudflare Functions)
// In development, use VITE_API_BASE_URL if set, otherwise default to /api (proxy)
const getApiBaseUrl = (): string => {
  // Always check runtime hostname first - this is the source of truth
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    
    // If we're on a production domain, ALWAYS use /api (ignore any env vars)
    if (hostname === 'open1031.pages.dev' || 
        hostname.endsWith('.pages.dev') ||
        hostname === 'open1031.ai' ||
        hostname.endsWith('.open1031.ai') ||
        hostname.includes('cloudflarepages.dev')) {
      return '/api'
    }
    
    // NEVER use localhost URLs in production - they cause CORS issues
    if (import.meta.env.PROD) {
      return '/api'
    }
    
    // If we're on localhost in dev, use /api (Vite proxy)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/api'
    }
  }
  
  // In development, check if VITE_API_BASE_URL is set and not localhost
  const envUrl = import.meta.env.VITE_API_BASE_URL
  // NEVER use localhost URLs - they cause CORS issues
  if (envUrl && 
      !envUrl.includes('localhost') && 
      !envUrl.includes('127.0.0.1') &&
      !envUrl.startsWith('http://localhost') &&
      !envUrl.startsWith('https://localhost')) {
    return envUrl
  }
  
  // Default to relative /api (will use Vite proxy in dev, Cloudflare Functions in prod)
  return '/api'
}

const api = axios.create({
  baseURL: '/api', // Default, will be overridden by interceptor
  headers: {
    'Content-Type': 'application/json',
  },
})

// Ensure baseURL is always correct on each request
api.interceptors.request.use((config) => {
  // Recompute baseURL on each request to ensure it's always correct
  const baseURL = getApiBaseUrl()
  config.baseURL = baseURL
  
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export interface AdminStats {
  totalUsers: number
  totalExchanges: number
  totalAccounts: number
  verifiedUsers: number
  activeExchanges: number
  pendingVerifications: number
  kycComplete: number
}

export interface AdminUser extends User {
  exchangeCount?: number
  accountCount?: number
}

export interface AdminUsersResponse {
  users: AdminUser[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminExchange {
  id: string
  accountId: string
  name: string
  status: string
  accountName?: string
  account?: {
    id: string
    name: string
  } | null
  owner?: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
  userCount?: number
  createdAt: string
  updatedAt: string
}

export interface AdminExchangesResponse {
  exchanges: AdminExchange[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export const adminApi = {
  async getStats(): Promise<AdminStats> {
    const response = await api.get('/admin/stats')
    return response.data
  },

  async getUsers(params?: {
    search?: string
    page?: number
    limit?: number
  }): Promise<AdminUsersResponse> {
    const response = await api.get('/admin/users', { params })
    return response.data
  },

  async getUser(userId: string): Promise<AdminUser & { exchanges?: any[], accounts?: any[] }> {
    const response = await api.get(`/admin/users/${userId}`)
    return response.data
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const response = await api.patch(`/admin/users/${userId}`, updates)
    return response.data.user
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`)
  },

  async resetDemoUser(userId: string): Promise<{ user: User, message: string }> {
    const response = await api.post(`/admin/users/${userId}/reset-demo`)
    return response.data
  },

  async getExchanges(params?: {
    search?: string
    status?: string
    page?: number
    limit?: number
  }): Promise<AdminExchangesResponse> {
    const response = await api.get('/admin/exchanges', { params })
    return response.data
  },

  async getExchange(exchangeId: string): Promise<AdminExchange & { account?: any, permissions?: any[] }> {
    const response = await api.get(`/admin/exchanges/${exchangeId}`)
    return response.data
  },

  async updateExchange(exchangeId: string, updates: Partial<AdminExchange>): Promise<AdminExchange> {
    const response = await api.patch(`/admin/exchanges/${exchangeId}`, updates)
    return response.data.exchange
  },

  async deleteExchange(exchangeId: string): Promise<void> {
    await api.delete(`/admin/exchanges/${exchangeId}`)
  },

  async getAccounts(): Promise<{ accounts: any[] }> {
    const response = await api.get('/admin/accounts')
    return response.data
  },

  async getDocuments(): Promise<{ documents: any[], total: number }> {
    const response = await api.get('/admin/documents')
    return response.data
  }
}

